"use client";

import { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";

interface BillingToggleProps {
  cadence: "annual" | "monthly";
  onCadenceChange: (cadence: "annual" | "monthly") => void;
  annualSavings: number;
}

export function BillingToggle({
  cadence,
  onCadenceChange,
  annualSavings,
}: BillingToggleProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [indicator, setIndicator] = useState({ left: 0, width: 0 });

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const activeBtn =
      container.querySelector<HTMLButtonElement>(`[data-active="true"]`);
    if (!activeBtn) return;
    setIndicator({
      left: activeBtn.offsetLeft,
      width: activeBtn.offsetWidth,
    });
  }, [cadence]);

  return (
    <div className="flex flex-col items-start gap-2 sm:flex-row sm:items-center sm:gap-3">
      <div
        ref={containerRef}
        className="relative inline-flex rounded-md bg-surface-inset p-0.5 dark:bg-[#18181a]"
      >
        {indicator.width > 0 && (
          <div
            className="absolute top-0.5 bottom-0.5 rounded-[5px] border border-border bg-background shadow-sm transition-all duration-200 ease-out dark:border-white/10 dark:bg-white/3"
            style={{ left: indicator.left, width: indicator.width }}
          />
        )}
        <button
          data-active={cadence === "monthly"}
          onClick={() => onCadenceChange("monthly")}
          className={cn(
            "relative z-10 cursor-pointer rounded-[5px] px-3 py-1 text-[13px] font-medium transition-colors",
            cadence === "monthly"
              ? "text-heading dark:text-white"
              : "text-contrast/50 hover:text-contrast/70 dark:text-white/50 dark:hover:text-white/70",
          )}
        >
          Monthly
        </button>
        <button
          data-active={cadence === "annual"}
          onClick={() => onCadenceChange("annual")}
          className={cn(
            "relative z-10 cursor-pointer flex items-center gap-1 rounded-[5px] pl-3 pr-1.5 py-1 text-[13px] font-medium transition-colors",
            cadence === "annual"
              ? "text-heading dark:text-white"
              : "text-contrast/50 hover:text-contrast/70 dark:text-white/50 dark:hover:text-white/70",
          )}
        >
          Annual
          {annualSavings > 0 && (
            <span className="rounded-sm border border-blue-500/15 bg-blue-500/10 px-1 py-px text-[11px] font-medium text-blue-700 dark:border-blue-400/10 dark:bg-blue-400/10 dark:text-[#87a5ff]">
              -${annualSavings}
            </span>
          )}
        </button>
      </div>
      <span className="hidden sm:block text-sm text-contrast/60 dark:text-white/70">
        Save ${annualSavings} per seat with annual billing.
      </span>
    </div>
  );
}
